import React, { useContext } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaArrowRight, FaVideo, FaBookOpen } from "react-icons/fa";

import videosImg from "../../../assets/course_content_tab/videos.png";
import materialsImg from "../../../assets/course_content_tab/materials.jpg";

import { DarkModeContext } from "../../../Context/DarkModeContext.jsx";

function AdminCatalogue() {
  const { isDarkMode } = useContext(DarkModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();

  const courseName = location.state?.name;

  const cards = [
    {
      title: "Videos",
      description: "Manage lectures and video content for your courses.",
      image: videosImg,
      route: "videos",
      icon: FaVideo,
    },
    {
      title: "Materials",
      description: "Manage PDFs, notes, and downloadable course materials.",
      image: materialsImg,
      route: "materials",
      icon: FaBookOpen,
    },
  ];

  const goToNextPage = () => {
    navigate(+1);
  };

  const goToPreviousPage = () => {
    navigate("/catalogue");
  };

  const handleCardClick = (route) => {
    navigate(`/catalogue/${route}`);
  };

  const pathSegments = location.pathname.slice(1).split("/");

  const formattedPath = pathSegments
    .map((segment) => {
      if (segment === courseId && courseName) {
        return courseName;
      }

      return segment;
    })
    .join(" > ");

  return (
    <div
      className={`min-h-screen px-4 pb-10 pt-20 sm:px-6 lg:px-8 ${
        isDarkMode ? "bg-[#292929] text-white" : "bg-[#f6f7f9] text-slate-900"
      }`}
    >
      <div className="mx-auto w-full max-w-[1500px]">
        {/* Navigation */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goToPreviousPage}
              aria-label="Go back"
              className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-all duration-200 ${
                isDarkMode
                  ? "border-white/10 bg-[#1e1e1e] text-white hover:bg-white/10"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              <IoIosArrowBack />
            </button>

            <button
              type="button"
              onClick={goToNextPage}
              aria-label="Go forward"
              className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-all duration-200 ${
                isDarkMode
                  ? "border-white/10 bg-[#1e1e1e] text-white hover:bg-white/10"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              <IoIosArrowForward />
            </button>
          </div>

          <div
            className={`text-sm capitalize ${
              isDarkMode ? "text-gray-400" : "text-slate-500"
            }`}
          >
            {formattedPath}
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <p
            className={`mb-2 text-xs font-semibold uppercase tracking-[0.18em] ${
              isDarkMode ? "text-gray-400" : "text-slate-500"
            }`}
          >
            Course Content
          </p>

          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Manage Catalogue
          </h1>

          <p
            className={`mt-3 max-w-xl text-sm leading-relaxed ${
              isDarkMode ? "text-gray-400" : "text-slate-500"
            }`}
          >
            Choose the type of course content you want to manage.
          </p>
        </div>

        {/* Cards */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.08,
              },
            },
          }}
          className="grid grid-cols-1 gap-6 lg:grid-cols-2"
        >
          {cards.map((card) => {
            const Icon = card.icon;

            return (
              <motion.button
                key={card.route}
                type="button"
                onClick={() => handleCardClick(card.route)}
                variants={{
                  hidden: {
                    opacity: 0,
                    y: 20,
                  },
                  show: {
                    opacity: 1,
                    y: 0,
                  },
                }}
                transition={{
                  duration: 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -4 }}
                className={`group overflow-hidden rounded-3xl border text-left transition-shadow duration-300 hover:shadow-xl ${
                  isDarkMode
                    ? "border-white/10 bg-[#1e1e1e]"
                    : "border-slate-200 bg-white"
                }`}
              >
                {/* Artwork */}
                <div className="relative h-[220px] overflow-hidden sm:h-[260px]">
                  <img
                    src={card.image}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />

                  <div
                    className={`absolute inset-0 ${
                      isDarkMode
                        ? "bg-gradient-to-t from-black/70 via-black/15 to-transparent"
                        : "bg-gradient-to-t from-black/60 via-black/10 to-transparent"
                    }`}
                  />

                  <div className="absolute bottom-5 left-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-xl text-white shadow-lg backdrop-blur-md">
                    <Icon aria-hidden="true" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex items-center justify-between gap-5 p-6">
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold sm:text-2xl">
                      {card.title}
                    </h2>

                    <p
                      className={`mt-2 text-sm leading-relaxed ${
                        isDarkMode ? "text-gray-400" : "text-slate-500"
                      }`}
                    >
                      {card.description}
                    </p>
                  </div>

                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-all duration-300 group-hover:translate-x-1 ${
                      isDarkMode
                        ? "bg-white/10 text-white group-hover:bg-white/15"
                        : "bg-slate-100 text-slate-700 group-hover:bg-[#06315B] group-hover:text-white"
                    }`}
                  >
                    <FaArrowRight />
                  </span>
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}

export default AdminCatalogue;
